import { useEffect, useState } from "react";
import { useQuote } from "./QuoteContext";
import {  doc, getDoc } from "firebase/firestore";
import { auth, db, functions } from "../../utils/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { X, ChevronDown, ChevronUp, Send, Loader2, FileText, ShoppingBag } from "lucide-react";
import { httpsCallable } from "firebase/functions";

export default function QuotePopup() {
  const { isOpen, closeQuote, selectedProduct } = useQuote();
  const product = selectedProduct;
  const [showList, setShowList] = useState(false);
  const [loading, setLoading] = useState(false);
  const [qty, setQty] = useState(1);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    mobile: "",
    company: "",
    address: "",
    city: "",
    state: "",
    district: "",
    postal: "",
    message: "",
    usage: "",
  });

  const clearForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      mobile: "",
      company: "",
      address: "",
      city: "",
      state: "",
      district: "",
      postal: "",
      message: "",
      usage: "",
    });
    closeQuote();
  };

  useEffect(() => {
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        let userRef = doc(db, "users", user.uid);
        let userDoc = await getDoc(userRef);
        if (!userDoc.exists()) {
          userRef = doc(db, "users", user.email.toLowerCase());
          userDoc = await getDoc(userRef);
        }
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const fullName = userData.name || "";
          const phoneNum = userData.mobileNumber || userData.mobile || "";
          const emailAddr = userData.email || user.email || "";

          setFormData((prev) => ({
            ...prev,
            name: fullName,
            email: emailAddr,
            phone: phoneNum,
            mobile: phoneNum,
            uid: user.uid,
            status: "requested",
            createdAt: new Date(),
          }));
        }
      } else {
        clearForm();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleQuantity = (type) => {
    setQty((prev) => (type === "inc" ? prev + 1 : Math.max(1, prev - 1)));
  };

  const sanitizeForFirestore = (obj) => {
    if (obj === null || typeof obj !== 'object') return obj;
    if (obj instanceof Date) return obj;
    if (Array.isArray(obj)) return obj.map(sanitizeForFirestore);

    const clean = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const val = obj[key];
        if (val !== undefined) {
          clean[key] = sanitizeForFirestore(val);
        }
      }
    }
    return clean;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let productsPayload = [];
      if (selectedProduct?.cart) {
        productsPayload = (selectedProduct.products || []).map((item) => ({
          productName: item.productName || item.product_name || "",
          productId: item.productId || "",
          quantity: item.cart_quantity || item.quantity || 1,
        }));
      } else {
        productsPayload = [
          {
            productName: product?.productName || product?.title || product?.name || "Product",
            productId: product?.id || product?.productId || product?.objectID || "",
            quantity: qty,
          },
        ];
      }

      const payload = sanitizeForFirestore({
        ...formData,
        products: productsPayload,
        status: 'requested'
      });
      const addQuoteRequestFn = httpsCallable(functions, "addQuoteRequest")
      const result = await addQuoteRequestFn(payload)
      if (result.data.success) {
        clearForm();
      }
    } catch (err) {
      console.error("Error submitting quote request:", err);
      alert("Failed to submit quote request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full px-3.5 py-2.5 text-sm font-semibold text-[#2C3E2B] bg-[#f8faf8] border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2C3E2B]/30 focus:border-[#2C3E2B] placeholder:text-slate-400 placeholder:font-normal transition";

  const fields = [
    { name: "company", type: "text", placeholder: "Company / Business Name" },
    { name: "mobile", type: "tel", placeholder: "Phone Number" },
    { name: "address", type: "text", placeholder: "Business Address" },
    { name: "city", type: "text", placeholder: "City" },
    { name: "state", type: "text", placeholder: "State" },
    { name: "district", type: "text", placeholder: "District" },
    { name: "postal", type: "text", placeholder: "Postal Code" },
  ];

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-[110] p-4">
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-[720px] max-h-[88vh] flex flex-col overflow-hidden"
        style={{ animation: "fadeInScale .2s ease" }}
      >
        {/* ── Header ── */}
        <div className="bg-[#2C3E2B] px-6 py-5 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/15 rounded-xl flex items-center justify-center">
              <FileText className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-base font-black text-white leading-tight m-0">
                Request a Quote
              </h2>
              <p className="text-[11px] text-white/60 font-semibold m-0">
                We'll get back to you within 24 hours
              </p>
            </div>
          </div>
          <button
            onClick={clearForm}
            className="w-8 h-8 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition border-none cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* Product / Cart section */}
          {selectedProduct?.cart ? (
            <div className="bg-[#f8faf8] border border-slate-200 rounded-2xl overflow-hidden">
              <button
                type="button"
                onClick={() => setShowList(!showList)}
                className="w-full flex items-center justify-between px-5 py-3.5 bg-transparent border-none cursor-pointer text-left"
              >
                <div className="flex items-center gap-2">
                  <ShoppingBag className="w-4 h-4 text-[#2C3E2B]" />
                  <span className="text-sm font-black text-[#2C3E2B]">
                    Cart Items ({selectedProduct.products?.length || 0})
                  </span>
                </div>
                {showList ? (
                  <ChevronUp className="w-4 h-4 text-slate-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                )}
              </button>
              {showList && (
                <table className="w-full text-sm border-t border-slate-200">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="px-4 py-2.5 text-left text-[11px] font-black text-slate-500 uppercase tracking-wider">Product</th>
                      <th className="px-4 py-2.5 text-left text-[11px] font-black text-slate-500 uppercase tracking-wider">Category</th>
                      <th className="px-4 py-2.5 text-left text-[11px] font-black text-slate-500 uppercase tracking-wider">Qty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {selectedProduct.products?.map((item, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-4 py-2.5 font-semibold text-[#2C3E2B] text-xs">
                          <div>{item?.productName || item?.product_name || item?.product?.title || item?.name || "—"}</div>
                          {(item?.productId || item?.code) && (
                            <div className="text-[10px] text-slate-400 font-normal mt-0.5">
                              ID: {item.productId || item.code}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-slate-400 text-xs">
                          {item?.product?.category || item?.product?.brand || item?.category || "N/A"}
                        </td>
                        <td className="px-4 py-2.5 font-bold text-[#2C3E2B] text-xs">
                          {item?.quantity || item?.cart_quantity || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            <div className="bg-[#f8faf8] border border-slate-200 rounded-2xl p-4">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-white rounded-xl border border-slate-200 flex items-center justify-center p-2 flex-shrink-0">
                  <img
                    src={product?.image || product?.thumb_url || "/logo.png"}
                    alt={product?.productName || "Product"}
                    className="w-full h-full object-contain"
                    onError={(e) => { e.target.onerror = null; e.target.src = "/logo.png"; }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-extrabold text-[#2C3E2B] leading-snug m-0">
                    {product?.productName || "Product"}
                  </h3>
                  <p className="text-xs text-slate-400 font-semibold mt-0.5">
                    {product?.categoryName || "N/A"}
                    {product?.type ? ` · ${product.type}` : ""}
                  </p>
                  {/* Qty selector */}
                  <div className="flex items-center gap-2 mt-3">
                    <span className="text-[11px] font-black text-slate-400 uppercase tracking-wider">Qty</span>
                    <div className="flex items-center bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs">
                      <button
                        type="button"
                        onClick={() => handleQuantity("dec")}
                        className="px-3 py-1.5 text-slate-500 hover:text-[#2C3E2B] hover:bg-slate-50 font-bold text-base border-none bg-transparent cursor-pointer transition"
                      >
                        −
                      </button>
                      <span className="px-3 py-1.5 font-black text-sm text-[#2C3E2B] border-x border-slate-200 min-w-[2.5rem] text-center">
                        {qty}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleQuantity("inc")}
                        className="px-3 py-1.5 text-slate-500 hover:text-[#2C3E2B] hover:bg-slate-50 font-bold text-base border-none bg-transparent cursor-pointer transition"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {/* Usage */}
              <textarea
                name="usage"
                placeholder="How often do you order this item? (optional)"
                value={formData.usage}
                onChange={handleChange}
                rows={2}
                className={`${inputClass} mt-4 resize-none`}
              />
            </div>
          )}

          {/* Customer Information */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <span className="text-[11px] font-black text-slate-400 uppercase tracking-wider">
                Contact Details
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {fields.map((field) => (
                <input
                  key={field.name}
                  type={field.type}
                  name={field.name}
                  placeholder={field.placeholder}
                  value={formData[field.name] || ""}
                  onChange={handleChange}
                  required
                  className={inputClass}
                />
              ))}
            </div>

            <textarea
              name="message"
              placeholder="Additional message or requirements (optional)"
              value={formData.message}
              onChange={handleChange}
              rows={3}
              className={`${inputClass} resize-none`}
            />

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3.5 bg-[#2C3E2B] hover:bg-[#1e2d1e] text-white font-black text-sm rounded-2xl border-none cursor-pointer transition shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center justify-center gap-2 ${loading ? "opacity-70 cursor-not-allowed" : ""
                }`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit Quote Request
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.96) translateY(8px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);   }
        }
      `}</style>
    </div>
  );
}
